import React from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@shadcn-in-nx/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn-in-nx/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { toast } from "sonner";

function renderJsonAsTable(data) {
    if (Array.isArray(data)) {
        return (
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-16">Index</TableHead>
                        <TableHead>Value</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.map((item, index) => (
                        <TableRow key={index}>
                            <TableCell className="font-medium">{index}</TableCell>
                            <TableCell className="font-mono text-sm">
                                {typeof item === "object" ? JSON.stringify(item) : String(item)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }

    if (typeof data === "object" && data !== null) {
        return (
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Key</TableHead>
                        <TableHead>Value</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {Object.entries(data).map(([key, value]) => (
                        <TableRow key={key}>
                            <TableCell className="font-medium">{key}</TableCell>
                            <TableCell className="font-mono text-sm">
                                {typeof value === "object" ? JSON.stringify(value) : String(value)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }

    return <div className="text-muted-foreground">No data</div>;
}

function ComplexJsonDialog({ dialog, onClose, onSave }) {
    const [editText, setEditText] = React.useState("");

    React.useEffect(() => {
        if (dialog) {
            setEditText(
                typeof dialog.value === "object"
                    ? JSON.stringify(dialog.value, null, 2)
                    : String(dialog.value ?? "")
            );
        }
    }, [dialog]);

    const handleSave = async () => {
        try {
            const parsed = JSON.parse(editText);
            await onSave(dialog.recordId, parsed, dialog.columnName);
            onClose();
            toast.success("Saved successfully");
        } catch {
            toast.error("Invalid JSON");
        }
    };

    return (
        <Dialog open={dialog?.open || false} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Edit {dialog?.columnName}</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="raw" className="flex-1 flex flex-col overflow-hidden">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="table">Table View</TabsTrigger>
                        <TabsTrigger value="raw">Raw JSON</TabsTrigger>
                    </TabsList>
                    <TabsContent value="table" className="flex-1 overflow-auto mt-4">
                        {dialog && renderJsonAsTable(dialog.value)}
                    </TabsContent>
                    <TabsContent value="raw" className="flex-1 overflow-auto mt-4">
                        <Textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="font-mono text-sm min-h-[400px] resize-none"
                            placeholder="Edit JSON here..."
                        />
                    </TabsContent>
                </Tabs>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSave}>Save Changes</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default React.memo(ComplexJsonDialog);
